<template>
  <el-card class="box-card full-screen">
    <div ref="pageHead" class="table-page-head">
      <div class="cb-summary">
        <div class="cb-tile">
          <div class="cb-tile-label">{{ $t("platform") }}</div>
          <div class="cb-tile-value">FCFA {{ fmt(platformTotal) }}</div>
        </div>
        <div class="cb-tile">
          <div class="cb-tile-label">
            {{ $t("directSaleStore") }} ({{ agencyCount }})
          </div>
          <div class="cb-tile-value">FCFA {{ fmt(agenciesTotal) }}</div>
        </div>
        <div class="cb-tile cb-tile--total">
          <div class="cb-tile-label">{{ $t("totalCommissionAmount") }}</div>
          <div class="cb-tile-value">FCFA {{ fmt(grandTotal) }}</div>
        </div>
      </div>
      <el-form :inline="true" size="small" @submit.native.prevent>
        <el-form-item :label="$t('orgName')">
          <el-input
            v-model="keyword"
            clearable
            :placeholder="$t('orgName')"
            style="width: 220px"
          />
        </el-form-item>
        <el-form-item>
          <el-checkbox v-model="hideEmpty">{{
            $t("hideZeroBalances")
          }}</el-checkbox>
        </el-form-item>
        <el-form-item>
          <el-button
            type="primary"
            icon="el-icon-refresh"
            :loading="isLoading"
            @click="load"
          >
            {{ $t("refresh") }}
          </el-button>
          <el-button
            icon="el-icon-plus"
            @click="$router.push({ name: 'operatingPaymentAdd' })"
          >
            {{ $t("operatingPaymentAdd") }}
          </el-button>
        </el-form-item>
      </el-form>
    </div>

    <div
      class="data-table"
      :style="{
        top: queryTableHeight,
        height: `calc(100% - ${queryTableHeight} - 20px)`,
      }"
    >
      <el-table
        :data="filtered"
        v-loading="isLoading"
        size="mini"
        height="100%"
        :default-sort="{ prop: 'accountAmount', order: 'descending' }"
        element-loading-background="rgba(255,255,255,0.4)"
      >
        <el-table-column type="index" align="center" width="50" />

        <el-table-column
          :label="$t('orgName')"
          min-width="240"
          prop="orgName"
          sortable
        >
          <template slot-scope="s">
            {{ s.row.orgName }}
            <span class="text-muted" style="font-size: 12px"
              >({{ s.row.orgInfoCode }})</span
            >
          </template>
        </el-table-column>

        <el-table-column :label="$t('type')" width="130">
          <template slot-scope="s">
            <el-tag size="mini" :type="s.row.orgType == 1 ? '' : 'info'">
              {{ s.row.orgType == 1 ? $t("platform") : $t("directSaleStore") }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column prop="cardNo" :label="$t('account')" min-width="190" />

        <el-table-column
          prop="accountAmount"
          :label="$t('commissionAmount')"
          min-width="170"
          align="right"
          sortable
          :sort-method="(a, b) => num(a.accountAmount) - num(b.accountAmount)"
        >
          <template slot-scope="s">
            <span
              :class="num(s.row.accountAmount) > 0 ? 'cb-amount' : 'text-muted'"
            >
              <span class="franc">FCFA</span> {{ fmt(s.row.accountAmount) }}
            </span>
          </template>
        </el-table-column>

        <el-table-column :label="$t('shareOfTotal')" width="190">
          <template slot-scope="s">
            <el-progress
              :percentage="share(s.row.accountAmount)"
              :stroke-width="10"
              :show-text="true"
            />
          </template>
        </el-table-column>

        <el-table-column
          prop="cashAmount"
          :label="$t('cashAmount')"
          min-width="170"
          align="right"
          sortable
          :sort-method="(a, b) => num(a.cashAmount) - num(b.cashAmount)"
        >
          <template slot-scope="s">
            <span class="text-muted">
              <span class="franc">FCFA</span> {{ fmt(s.row.cashAmount) }}
            </span>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </el-card>
</template>

<script>
import operatingPayment from "@/api/operatingPayment";

export default {
  name: "commissionBalances",
  data() {
    return {
      list: [],
      isLoading: false,
      keyword: "",
      hideEmpty: false,
      queryTableHeight: String,
    };
  },
  computed: {
    filtered() {
      const k = this.keyword.trim().toLowerCase();
      return this.list.filter((b) => {
        if (this.hideEmpty && this.num(b.accountAmount) <= 0) return false;
        if (!k) return true;
        return (
          (b.orgName || "").toLowerCase().includes(k) ||
          String(b.orgInfoCode || "").includes(k)
        );
      });
    },
    platformTotal() {
      return this.list
        .filter((b) => b.orgType == 1)
        .reduce((s, b) => s + this.num(b.accountAmount), 0);
    },
    agenciesTotal() {
      return this.list
        .filter((b) => b.orgType == 2)
        .reduce((s, b) => s + this.num(b.accountAmount), 0);
    },
    agencyCount() {
      return this.list.filter((b) => b.orgType == 2).length;
    },
    grandTotal() {
      return this.platformTotal + this.agenciesTotal;
    },
  },
  methods: {
    num(v) {
      return parseFloat(v) || 0;
    },
    fmt(n) {
      return Number(n || 0).formatMoney(0);
    },
    share(v) {
      if (this.grandTotal <= 0) return 0;
      return Math.round((this.num(v) / this.grandTotal) * 1000) / 10;
    },
    load() {
      this.isLoading = true;
      operatingPayment.commissionBalances({
        param: {},
        success: (res) => {
          this.isLoading = false;
          this.list = res.code === 200 && res.data ? res.data.list || [] : [];
        },
        failing: () => {
          this.isLoading = false;
        },
      });
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.queryTableHeight = this.$refs.pageHead.offsetHeight + 15 + "px";
    });
    this.load();
  },
};
</script>

<style lang="less" scoped>
.cb-summary {
  display: flex;
  gap: 16px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}
.cb-tile {
  flex: 1;
  min-width: 200px;
  padding: 12px 16px;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background: #fafafa;
  &--total {
    background: #f0f5ff;
    border-left: 3px solid #1164c4;
    .cb-tile-value {
      color: #1164c4;
    }
  }
}
.cb-tile-label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 4px;
}
.cb-tile-value {
  font-size: 18px;
  font-weight: 700;
  color: #303133;
}
.cb-amount {
  font-weight: 600;
  color: #303133;
}
</style>
